import { ChevronLeftIcon } from '@heroicons/react/outline';
import { ArrowCircleDownIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { Affix, Button, ConfigProvider, Modal, Spin, ThemeConfig } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import StartIcon from 'assets/Icons/OrangesLandingPage/StartIcon';
import TimeRunningClock from 'assets/Icons/TimeRunningClock';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { Montserrat } from 'next/font/google';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import ClaimTooltip from '@/components/BookLandingPageComp/SlidBookContainer/ClaimTooltip';
import RenderIf from '@/components/Common/RenderIf';

import { getGlobalCountry, isSubscriptionDisabled } from '@/atoms/countryState';
import { iframeModalAtom } from '@/atoms/pdfStates/iframeModalAtom';
import { productAccessEnroll } from '@/services/Subscribe';
import { getUserProfileDirect } from '@/services/user';
import { IProduct } from '@/utils/interfaces/Product';
import { IProductAccessCheck } from '@/utils/interfaces/Subscription';
import { formatPrice } from '@/utils/Product';

import PDFReaderIframe from '../../IFrame/PDFReaderIframe';
import ShareButton from '../../PdfReader/ShareButton';
import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../shared/formateErrorObject';
import { notify } from '../../shared/notify';
import SecurePaymentV2 from '../../../../assets/Icons/SecurePaymentV2';
import WhiteKeyIcon from '../../../../assets/Icons/WhiteKeyIcon';
import { authModalControl } from '../../../atoms/authModalControl';
import { localUserID, user } from '../../../atoms/user';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import {
  addCouponCode,
  addToCart,
  addToRecentlyViewed,
} from '../../../services/Cart';
import { hasPreviouslySubscribed, isSubscriptionActive } from '../../../utils';
import { AddToCartProps } from '../../../utils/interfaces/cart';
import {
  IBookLandingPage,
  ISlidBook,
} from '../../../utils/interfaces/landingPage';
import { User } from '../../../utils/interfaces/User';

interface SlidBookContainerProps {
  landingPageData: any;
  slidBookContainerData: ISlidBook;
  token?: string;
  purchasedData: {
    hardProduct: {
      purchased: boolean;
    };
    softProduct: {
      purchased: boolean;
    };
  };
  digitalCopyCountryData?: any;
  hardCopyCountryData?: any;
  isPurchased: boolean;
  lastPageRead: number;
  bookLandingDetails?: IBookLandingPage;
  productAccessCheckData?: IProductAccessCheck | null;
  ecomProductData: IProduct | null | undefined;
  isOrange?: boolean;
}

const config: ThemeConfig = {
  token: {
    colorPrimary: 'white',
    fontFamily: 'inherit',
  },
};

const montserrat = Montserrat({
  subsets: ['latin'],
  fallback: ['Segoe UI', 'Roboto', 'Arial', 'sans-serif'],
  display: 'swap',
});

export const extractSlugAndProductId = (slugWithId: string): string => {
  const cleanSlug = slugWithId?.split('?')[0]?.replace(/\/$/, '');
  if (!cleanSlug) return '';

  const productId = cleanSlug.slice(cleanSlug.lastIndexOf('-') + 1);

  const isValidObjectId = /^[a-fA-F0-9]{24}$/.test(productId);

  const urlParams = new URLSearchParams(slugWithId);
  const queryProductId = urlParams.get('productId');

  return isValidObjectId ? productId : (queryProductId as string);
};

const SlidBookContainer: React.FC<SlidBookContainerProps> = ({
  slidBookContainerData,
  token,
  purchasedData,
  digitalCopyCountryData,
  hardCopyCountryData,
  lastPageRead,
  bookLandingDetails,
  productAccessCheckData,
  ecomProductData,
  landingPageData,
  isOrange = false,
}) => {
  console.log({ landingPageData })
  // const [selectedOption, setSelectOption] = useState<{
  //   type: 'hardCopy' | 'softCopy';
  //   productId: number;
  //   price: number;
  // }>({
  //   type: 'hardCopy',
  //   productId: 0,
  //   price: 0,
  // });
  const [isModalVisible, setIsModalVisible] = useRecoilState(iframeModalAtom);
  const [loadingAddToCart, setLoadingAddToCart] = useState({
    value: false,
    id: 0,
  });
  const [isFlipped, setIsFlipped] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [divHeight, setDivHeight] = useState('100%');
  const [screenHeight, setScreenHeight] = useState(0);
  const [loadedImages, setLoadedImages] = useState({
    front: false,
    back: false,
    side: false,
  });

  const [, setVisible] = useRecoilState(authModalControl);

  const localUserId = useRecoilValue(localUserID);
  const [userDataState, setUserDataState] = useRecoilState<User | undefined>(user);
  const userData: User | undefined = userDataState;
  const divRef = useRef<HTMLDivElement>(null);
  const screens = useBreakpoint();
  const router = useRouter();
  const { short } = router.query;

  const hasDigitalPair = Boolean(ecomProductData?.digital_pair);
  const hasUpgradeOffer = Boolean(
    ecomProductData?.upgrade_offer &&
      (ecomProductData.upgrade_offer.eligible === undefined ||
        ecomProductData.upgrade_offer.eligible === true),
  );

  const digitalId = Number(
    ecomProductData?.digital_pair?.id ||
      slidBookContainerData?.digitalProductId ||
      bookLandingDetails?.digitalProductId,
  );

  const formatBookTypeTitle = (type?: string) => {
    if (!type) {
      return bookLandingDetails?.bookType === 'paperBack' ? 'Paperback' : 'Hardcopy';
    }
    const normalized = type.trim().toLowerCase();
    if (normalized === 'hardcover' || normalized === 'hard_cover') {
      return 'Hardcopy';
    }
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const mainButtonTitle = formatBookTypeTitle(ecomProductData?.book_type);

  const effectiveDigitalCopyData = useMemo(() => {
    if (
      digitalCopyCountryData &&
      digitalCopyCountryData.is_available !== null
    ) {
      return digitalCopyCountryData;
    }
    if (ecomProductData?.digital_pair) {
      return {
        stroked_price: formatPrice(ecomProductData.digital_pair.stroked_price),
        main_price: formatPrice(ecomProductData.digital_pair.main_price),
        currency_symbol: ecomProductData.digital_pair.currency_symbol || '₹',
        is_available:
          (ecomProductData.digital_pair.is_available &&
            ecomProductData.digital_pair.i18n_attributes) ||
          ecomProductData.digital_pair.is_available ||
          null,
        i18n_attributes: ecomProductData.digital_pair.i18n_attributes,
      };
    }
    return digitalCopyCountryData;
  }, [digitalCopyCountryData, ecomProductData]);

  const hardCorporatePrice = useMemo(() => {
    const unitPrice =
      ecomProductData?.organization_pricing?.discounted_price ??
      ecomProductData?.i18n_attributes?.unit_price ??
      ecomProductData?.main_price ??
      1790;
    const regularPrice =
      ecomProductData?.organization_pricing?.original_price ??
      ecomProductData?.i18n_attributes?.regular_price ??
      ecomProductData?.stroked_price ??
      3990;
    return {
      unit_price: unitPrice,
      regular_price: regularPrice,
    };
  }, [ecomProductData]);

  const digitalCorporatePrice = useMemo(() => {
    const unitPrice =
      ecomProductData?.digital_pair?.organization_pricing?.discounted_price ??
      ecomProductData?.digital_pair?.i18n_attributes?.unit_price ??
      ecomProductData?.digital_pair?.main_price ??
      990;
    const regularPrice =
      ecomProductData?.digital_pair?.organization_pricing?.original_price ??
      ecomProductData?.digital_pair?.i18n_attributes?.regular_price ??
      ecomProductData?.digital_pair?.stroked_price ??
      1990;
    return {
      unit_price: unitPrice,
      regular_price: regularPrice,
    };
  }, [ecomProductData]);

  const isOrgMember = Boolean(
    userData?.organization &&
    (userData.organization.role === 'member' ||
      userData.organization.role === 'admin') && landingPageData.bookType !== 'paperBack',
  );

  useEffect(() => {
    if (!userData) {
      getUserProfileDirect()
        .then((res) => {
          if (res?.data) {
            setUserDataState(res.data);
          }
        })
        .catch(() => { });
    }
  }, [userData]);

  const isPreOrderAvailable = useMemo(() => {
    return (
      hardCopyCountryData?.is_preorder &&
      hardCopyCountryData?.is_available !== null
    );
  }, [hardCopyCountryData]);

  const allImagesLoaded =
    loadedImages.front && loadedImages.back && loadedImages.side;

  const showModal = () => {
    setIsModalVisible(true);
    const currentTime = new Date();

    triggerViewEvent(currentTime);
  };

  const handleOk = () => {
    setIsModalVisible(false);
    const currentTime = new Date();

    triggerViewEvent(currentTime);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    const currentTime = new Date();
    if (startTime) {
      triggerSessionEvent(startTime, currentTime);
    }
  };

  const claimHandle = (productId: number) => {
    router.push(`/subscription-checkout?productId=${productId}`);
  };

  // const handleOptionClick = ({
  //   type,
  //   productId,
  //   price,
  // }: {
  //   type: 'hardCopy' | 'softCopy';
  //   productId: number;
  //   price: number;
  // }) => {
  //   setSelectOption({ type, productId, price });
  // };

  // useEffect(() => {
  //   if (slidBookContainerData) {
  //     if (slidBookContainerData?.hardCoverAvailable !== false) {
  //       setSelectOption({
  //         type: 'hardCopy',
  //         productId: slidBookContainerData?.hardCoverProductId,
  //         price: slidBookContainerData?.hardCopyPrice?.discountedPrice,
  //       });
  //     } else {
  //       setSelectOption({
  //         type: 'softCopy',
  //         productId: slidBookContainerData?.digitalProductId,
  //         price: slidBookContainerData?.softCopyPrice?.discountedPrice,
  //       });
  //     }
  //   }
  // }, [slidBookContainerData?.hardCoverAvailable]);

  const handleApplyCoupon = async (coupon: string) => {
    try {
      await addCouponCode(coupon as string);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  const handleAddDataToCart = async ({
    payload,
    isBuyNow,
    bookData,
  }: {
    payload: AddToCartProps;
    isBuyNow?: boolean;
    bookData?: {
      bookName: string;
      productType?: string;
    };
  }) => {
    try {
      setLoadingAddToCart({ value: true, id: payload.id });

      const data = await addToCart(payload, isBuyNow);
      addToRecentlyViewed(payload.id, userData?.zl_uid);
      const isPaperBack =
        isOrange ||
        bookLandingDetails?.bookType === 'paperBack' ||
        landingPageData?.bookType === 'paperBack';

      if (bookData?.bookName) {
        if (isPaperBack) {
          notify(
            `${bookData?.bookName} added to cart successfully!`,
            'success',
          );
        } else if (bookData?.productType) {
          const isHard =
            bookData.productType.toLowerCase() === 'hardcopy';
          notify(
            `${bookData?.bookName} ${isHard ? 'Hard Copy' : 'Digital Copy'}  added to cart successfully!`,
            'success',
          );
        }
      }

      if (isBuyNow) {
        await handleApplyCoupon(
          hardCopyCountryData?.preorder_coupon_code || '',
        );
        router.push(
          `${process.env.NEXT_PUBLIC_CHECKOUT_ROUTE || '/checkout'}?cartId=${data?.data?.cart_id}&pre`,
        );
      } else {
        router.push(`/cart/`);
      }
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoadingAddToCart({ value: false, id: payload.id });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (divRef.current) {
        const screenHeight = window.innerHeight;
        setScreenHeight(window.innerHeight);

        const divHeight = divRef.current.clientHeight;

        if (divHeight > screenHeight) {
          setDivHeight('90vh');
        } else {
          setDivHeight('100%');
        }
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [screens]);

  const handleImageLoad = (imageType: 'front' | 'back' | 'side') => {
    setLoadedImages((prevState) => ({
      ...prevState,
      [imageType]: true,
    }));
  };

  const triggerViewEvent = (start: Date) => {
    setStartTime(start);

    const eventParams: EventDetails = {
      event_name: 'learn_view',
      source: 'books',
      source_type: router.query['cs']
        ? 'comingSoon'
        : router.query['swp']
          ? 'short'
          : 'long',
      source_id: extractSlugAndProductId(router.asPath) as string,
      sub_source: 'sample',
      sub_source_id: null,
      unit: 'boolean',
      value: 1,
    };


    if (ecomProductData?.display_name) {
      eventParams.source_name = ecomProductData?.display_name;
    }

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const triggerSessionEvent = (start: Date, end: Date) => {
    const sessionDuration = (end.getTime() - start.getTime()) / 1000;

    const eventParams: EventDetails = {
      event_name: 'learn_session',
      source: 'books',
      source_type: router.query['cs']
        ? 'comingSoon'
        : router.query['swp']
          ? 'short'
          : 'long',
      source_id: extractSlugAndProductId(router.asPath) as string,
      sub_source: 'sample',
      sub_source_id: null,
      unit: 'seconds',
      value: sessionDuration,
    };


    if (ecomProductData?.display_name) {
      eventParams.source_name = ecomProductData?.display_name;
    }

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
    setStartTime(null);
  };
  const handleMouseClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    setIsFlipped(true);
  };

  const handleTouchClick: React.TouchEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    setIsFlipped(true);
  };

  const handleMouseBackClick: React.MouseEventHandler<HTMLDivElement> = (
    event,
  ) => {
    event.preventDefault();
    setIsFlipped(false);
  };

  const handleTouchBackClick: React.TouchEventHandler<HTMLDivElement> = (
    event,
  ) => {
    event.preventDefault();
    setIsFlipped(false);
  };

  useEffect(() => {
    const bookWrap = document.querySelector('.book-wrap');
    if (bookWrap) {
      if (isFlipped) {
        bookWrap.classList.add('flip');
      } else {
        bookWrap.classList.remove('flip');
      }
    }
  }, [isFlipped]);

  useEffect(() => {
    if (allImagesLoaded) {
      const bookWrap = document.querySelector('.book-wrap');

      const handleMouseEnter = () => {
        if (bookWrap) {
          bookWrap.classList.add('rotate');
        }
      };

      const handleMouseLeave = () => {
        if (bookWrap) {
          bookWrap.classList.remove('rotate');
        }
      };

      if (window.innerWidth > 600) {
        if (bookWrap) {
          bookWrap.addEventListener(
            'mouseenter',
            handleMouseEnter as EventListener,
          );
          bookWrap.addEventListener(
            'mouseleave',
            handleMouseLeave as EventListener,
          );
        }
      }

      return () => {
        if (bookWrap) {
          bookWrap.removeEventListener(
            'mouseenter',
            handleMouseEnter as EventListener,
          );
          bookWrap.removeEventListener(
            'mouseleave',
            handleMouseLeave as EventListener,
          );
        }
      };
    } else return;
  }, [allImagesLoaded]);

  useEffect(() => {
    if (allImagesLoaded) {
      const bookWrap = document.querySelector('.book-wrap');

      const simulateRotationAndFlip = () => {
        if (!bookWrap) return;

        bookWrap.classList.add('rotate');

        setTimeout(() => {
          bookWrap.classList.remove('rotate');
          bookWrap.classList.add('flip');

          setTimeout(() => {
            bookWrap.classList.remove('flip');
          }, 2000);
        }, 500);
      };

      const simulationTimer = setTimeout(simulateRotationAndFlip, 1000);

      return () => {
        clearTimeout(simulationTimer);
      };
    } else {
      return;
    }
  }, [allImagesLoaded, router.isReady]);

  const keyCount = useMemo(() => {
    return (
      userData?.features?.reduce(
        (sum, i) => sum + (i?.available_quantity || 0),
        0,
      ) || 0
    );
  }, [userData]);

  const startReadingHandler = ({
    bookId,
    chapterId,
    chapterPageNumber,
    bookType,
  }: {
    bookId: string;
    chapterId?: string;
    chapterPageNumber?: number;
    bookType?: string;
  }) => {
    let url = `/pdf/${bookId}`;
    const params = new URLSearchParams();

    if (chapterId !== undefined) {
      params.append('chapterId', chapterId);
      if (chapterPageNumber !== undefined) {
        params.append('chapterPageNumber', chapterPageNumber?.toString());
      }
    } else if (chapterPageNumber !== undefined) {
      params.append('chapterPageNumber', chapterPageNumber?.toString());
    }

    if (bookType === 'short') {
      params.append('samplePdf', 'true');
    }

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    router.push(url);
  };

  const productAccessEnrollHandle = async () => {
    if (
      productAccessCheckData?.can_access &&
      !productAccessCheckData.is_purchased &&
      !productAccessCheckData.user_product &&
      bookLandingDetails?.digitalProductId
    ) {
      try {
        await productAccessEnroll(
          bookLandingDetails?.digitalProductId?.toString(),
        );
      } catch (error) {
        console.error(error);
      }
    }
  };

  const claimSoftButton = (
    <ButtonDefault
      disabled={purchasedData?.softProduct?.purchased || keyCount === 0}
      variant={ButtonVariants.PRIMARY}
      is3D
      size={0}
      className='w-full px-3 py-[9px] disabled:shadow-none disabled:opacity-30 disabled:bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)]'
      onClick={() => claimHandle(slidBookContainerData?.digitalProductId)}
    >
      <p className='text-sm leading-[22px] font-semibold text-white '>
        Claim Digital Copy with 1
      </p>
      <WhiteKeyIcon />
    </ButtonDefault>
  );

  const claimHardButton = (
    hardCopyCountryData?.is_available ?
      <ButtonDefault
        disabled={keyCount === 0}
        size={0}
        variant={ButtonVariants.PRIMARY}
        is3D
        className='w-full px-3 py-[9px] disabled:shadow-none disabled:opacity-30 disabled:bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)]'
        onClick={() => claimHandle(slidBookContainerData?.hardCoverProductId)}
      >
        <p className='text-sm leading-[22px] font-semibold text-white '>
          Claim Hard Copy with 1
        </p>
        <WhiteKeyIcon />
      </ButtonDefault> : <></>
  );

  const claimSubscriptionButton = (
    <ShouldRender check={!isSubscriptionDisabled()}>
      {isSubscriptionActive(userData) ? (
        userData?.subscription?.snapshot?.rules?.[0]?.physical ? (
          purchasedData.hardProduct.purchased ? (
            <ClaimTooltip
              title='Already Purchased'
              message='You have already purchased this product.'
            >
              {claimHardButton}
            </ClaimTooltip>
          ) : keyCount ? (
            claimHardButton
          ) : (
            <ClaimTooltip
              title='Insufficient Credit'
              message='You don’t have enough credit to claim this product. Kindly wait till next month.'
            >
              {claimHardButton}
            </ClaimTooltip>
          )
        ) : userData?.subscription?.snapshot?.rules?.[0]?.digital ? (
          purchasedData.softProduct.purchased ? (
            <ClaimTooltip
              title='Already Purchased'
              message='You have already purchased this product.'
            >
              {claimSoftButton}
            </ClaimTooltip>
          ) : keyCount ? (
            claimSoftButton
          ) : (
            <ClaimTooltip
              title='Insufficient Credit'
              message='You don’t have enough credit to claim this product. Kindly wait till next month.'
            >
              {claimSoftButton}
            </ClaimTooltip>
          )
        ) : (
          <ClaimTooltip
            title='Subscription Inactive'
            message='Your subscription is currently inactive. Please renew to continue.'
          >
            {claimHardButton}
          </ClaimTooltip>
        )
      ) : (
        <ButtonDefault
          isWidthFull={true}
          size={0}
          variant={ButtonVariants.PRIMARY}
          is3D={true}
          className='w-full hidden'
          onClick={() => router.push('/subscription')}
        >
          <div className='w-full flex justify-between items-center gap-1 py-2.5 px-3.5'>
            <span className='text-base font-semibold'>Subscribe</span>
            <div className='flex items-center gap-1'>
              <div className='px-2.5 py-0.5 rounded-full bg-black/30'>
                <p className='text-white font-semibold text-xs/5'>
                  {process.env.NEXT_PUBLIC_SUBSCRIPTION_OFFER}
                </p>
              </div>
              <ChevronRightIcon className='w-6 h-6' />
            </div>
          </div>
        </ButtonDefault>
      )}

      <ShouldRender
        check={
          !isSubscriptionActive(userData) && hasPreviouslySubscribed(userData)
        }
      >
        {/* <ClaimTooltip
          title='Subscription Inactive'
          message='Your subscription has expired. Please resubscribe to unlock Gems and claim this product.'
        > */}
        {claimHardButton}
        {/* </ClaimTooltip> */}
      </ShouldRender>
    </ShouldRender>
  );

  const paymentButton = (
    <ShouldRender check={bookLandingDetails?.bookType !== 'paperBack'}>
      <div className={clsx(screenHeight > 800 ? 'mt-4' : 'mt-4', 'w-full ')}>
        <div>
          {claimSubscriptionButton}
        </div>
      </div>
    </ShouldRender>
  );

  const isComingSoon = slidBookContainerData.bookType === 'comingSoon';

  const sideHeightClass = isComingSoon
    ? 'h-[238px] '
    : screenHeight > 800
      ? 'h-[306px] 2xl:h-[332px]'
      : 'h-[306px]';
  const containerClass = clsx(
    isComingSoon
      ? 'max-w-[168px] translate-y-3 2xl:translate-y-0'
      : screenHeight > 800
        ? 'max-w-[236px] translate-x-5 min-w-[250px] 2xl:min-w-[280px]'
        : 'max-w-[216px] md:max-w-[216px]',
    'relative w-full cursor-pointer overflow-hidden rounded-lg transition-transform duration-300 ease-in-out',
  );



  return (
    <ConfigProvider theme={config}>
      <div
        className={clsx(
          'relative w-full flex-col flex  items-center justify-center lg:h-screen lg:translate-x-7 2xl:translate-x-0 xl:min-h-full overflow-hidden',
        )}
      >
        <div className='w-full block '>
          <ShouldRender check={!screens?.lg && !short}>
            {isSubscriptionActive(userData) ? null : (
              <ButtonDefault
                isWidthFull={true}
                size={0}
                variant={ButtonVariants.PRIMARY}
                is3D={true}
                className='w-full rounded-none py-0 px-0 pr-0 pl-0  hidden'
                onClick={() => router.push('/subscription')}
              >
                <div className='w-full flex justify-between items-center gap-1 py-2 px-3.5'>
                  <span className='text-sm font-semibold'>Subscribe</span>
                  <div className='flex items-center gap-1'>
                    <div className='px-1.5 py-px rounded-full bg-black/30'>
                      <p className='text-white font-medium text-[10px]/none leading-[18px]'>
                        {process.env.NEXT_PUBLIC_SUBSCRIPTION_OFFER}
                      </p>
                    </div>
                    <ChevronRightIcon className='w-6 h-6' />
                  </div>
                </div>
              </ButtonDefault>
            )}
          </ShouldRender>
          <ShouldRender check={!screens?.lg && !short}>
            <div
              className={clsx(
                screenHeight > 800 ? 'pb-4 mt-4' : 'py-5',
                'flex justify-between items-center w-full px-3',
              )}
            >
              <ChevronLeftIcon
                onClick={() => router.back()}
                className='text-white w-[22px] h-[22px]'
              />
              <motion.div
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  borderRadius: '4px',
                  padding: '2px',
                  backgroundColor: '#001330',
                }}
              >
                <motion.div
                  initial={{ backgroundPosition: '0% 50%' }}
                  animate={{ backgroundPosition: '200% 50%' }}
                  transition={{
                    ease: 'linear',
                    duration: 3,
                    repeat: Infinity,
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '4px',
                    border: '2px solid transparent',
                    background:
                      'linear-gradient(90deg, rgba(35,122,255,0) 25%, rgba(35,122,255,1) 50%, rgba(35,122,255,0) 75%)',
                    backgroundSize: '200% 100%',
                    pointerEvents: 'none',
                  }}
                />
                <div
                  className='text-[#237AFF] whitespace-nowrap bg-[#001330] font-semibold py-2 px-3 text-xs flex'
                  style={{
                    borderRadius: '4px',
                    zIndex: 2,
                    position: 'relative',
                  }}
                >
                  {slidBookContainerData?.productHighlightText}
                </div>
              </motion.div>
              <ShareButton
                ecomProductData={ecomProductData}
                isShortBook={bookLandingDetails?.bookType === 'short'}
                isLoggedIN={!!userData?.zl_uid}
                type='book'
                isReadingScreen={true}
                isPdfReader={true}
              />
            </div>
          </ShouldRender>
        </div>
        <RenderIf condition={isOrange && !screens.lg}>
          <div className='absolute top-[76px] left-3 z-20'>
            <div
              className='bg-[#0F371E] inline-flex items-center gap-1 px-[4.5px] py-[5px] rounded-full'

            >
              <StartIcon />
              <span className='text-[#22C55E] text-[10px] font-semibold tracking-wide '>Authentic &amp; Original</span>
            </div>
          </div>
        </RenderIf>

        <div
          style={{
            padding: screens.lg ? '1px' : 'null',
            background: screens.lg
              ? !short
                ? 'linear-gradient(to top, rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0))'
                : 'linear-gradient(180deg, #000000 0%, #151515 100%)'
              : 'null',
            borderRadius: screens.lg ? '8px' : 'null',
          }}
          className='relative '
        >

          <div
            ref={divRef}
            style={{
              position: 'relative',
              height: divHeight,
              overflowY: 'auto',
              background: screens.lg
                ? !short
                  ? 'black'
                  : 'linear-gradient(180deg, #000000 0%, #151515 100%)'
                : 'null',
            }}
            className={twMerge(clsx(
              screenHeight > 800 ? 'md:py-4 lg:py-8' : 'pb-2 pt-6',
              short ? 'px-10 mt-5' : 'px-6 ',
              isComingSoon && 'min-w-[360px]',
              `rounded-[8px] h- no-scrollbar lg:overflow-auto h-[${divHeight}] relative `,
            ), isOrange ? 'pt-[64px]' : '')}
          >
            <RenderIf condition={isOrange && screens.lg}>
              <div className='absolute top-3 left-3 z-20'>
                <div
                  className='bg-[#0F371E] inline-flex items-center gap-1 px-[4.5px] py-[5px] rounded-full'

                >
                  <StartIcon />
                  <span className='text-[#22C55E] text-[10px] font-semibold tracking-wide '>Authentic &amp; Original</span>
                </div>
              </div>
            </RenderIf>
            <ShouldRender check={screens.lg}>
              <div
                className={clsx(
                  short
                    ? 'top-[3%] md:top-[8%] xl:top-[8%] 2xl:top-[7%] right-[4%] lg:right-[12%] 2xl:right-[8%]'
                    : ' top-[17%] md:top-[17%]  lg:top-[3%] 2xl:top-[10%] right-[4%] lg:right-[10%]',
                  'w-5 h-5 absolute  z-10 ',
                )}
              >
                <ShareButton
                  ecomProductData={ecomProductData}
                  isShortBook={bookLandingDetails?.bookType === 'short'}
                  isLoggedIN={!!userData?.zl_uid}
                  type='book'
                  isReadingScreen={true}
                  isPdfReader={true}
                />
              </div>
            </ShouldRender>
            <RenderIf condition={isOrange}>
              <div className='flex justify-center py-7'>
                <Image src={landingPageData?.form?.bookImage}
                  className='rounded-lg'
                  width={screens.xxl ? 236 : 181}
                  height={screens.xxl ? 358 : 280}
                  alt='Book Front'
                />
              </div>
            </RenderIf>

            <ShouldRender check={!short && !isOrange}>
              <div
                className={clsx(
                  screenHeight > 800 && 'mt-5 xl:-translate-x-2',
                  ' -translate-y-2 transition-transform duration-500',
                  screenHeight > 800 && !isFlipped && ' lg:translate-x-1',
                  !allImagesLoaded && 'hidden',
                )}
              >
                <div className={clsx('wrap', isComingSoon && 'coming-soon')}>
                  <div className='perspective z-40'>
                    <div className='book-wrap '>
                      {/* Book front */}
                      <div
                        className='book book-1'
                        onClick={handleMouseClick}
                        onTouchStart={handleTouchClick}
                      >
                        <Image
                          src={slidBookContainerData?.bookImage || ''}
                          alt='Book Front'
                          priority
                          width={isComingSoon ? 168 : 236}
                          height={isComingSoon ? 238 : 358}
                          className='book-image'
                          onLoad={() => handleImageLoad('front')}
                          style={{ height: 'auto' }}
                          sizes='(max-width: 768px) 100vw, 50vw'
                        />
                      </div>

                      {/* Book back */}
                      <div
                        className='book-back book-1'
                        onClick={handleMouseBackClick}
                        onTouchStart={handleTouchBackClick}
                      >
                        <Image
                          src={slidBookContainerData?.bookBackImage}
                          width={isComingSoon ? 168 : 236}
                          height={isComingSoon ? 238 : 358}
                          alt='Book Back'
                          priority
                          onLoad={() => handleImageLoad('back')}
                        />
                      </div>

                      <div
                        className={clsx(
                          sideHeightClass,
                          'title bg-transparent',
                        )}
                      >
                        <div className='relative w-full h-full'>
                          <Image
                            src={slidBookContainerData?.bookSideImage}
                            fill
                            alt='Book Side'
                            priority
                            sizes='(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw'
                            className={clsx(
                              isComingSoon
                                ? screenHeight > 800
                                  ? 'translate-y-[13px] 2xl:translate-y-[0px] '
                                  : 'translate-y-[13px]'
                                : '-translate-y-[2px]',
                            )}
                            onLoad={() => handleImageLoad('side')}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ShouldRender>
            <ShouldRender check={short}>
              <div
                className='flex justify-center items-center pb-8'
                onClick={handleMouseClick}
                onTouchStart={handleTouchClick}
              >
                <Image
                  src={slidBookContainerData?.bookImage}
                  width={screens.xxl ? 236 : 181}
                  height={screens.xxl ? 358 : 280}
                  alt='Book Front'
                  priority
                  onLoad={() => handleImageLoad('front')}
                />
              </div>
            </ShouldRender>

            <ShouldRender check={!allImagesLoaded && !short && !isOrange}>
              <div
                className=' w-full flex justify-center items-center
                      h-[350px]'
              >
                <Spin size='default' />
              </div>
            </ShouldRender>
            <ShouldRender check={!short}>
              <ShouldRender check={extractSlugAndProductId(router.asPath)}>
                <ShouldRender
                  check={bookLandingDetails?.bookType !== 'paperBack'}
                >
                  <div
                    className={clsx(
                      screenHeight > 800 ? 'mt-4 2xl:mt-14' : 'lg:mt-10',

                      !isComingSoon &&
                      screenHeight > 800 &&
                      (isFlipped
                        ? '-translate-x-0 xl:-translate-x-5'
                        : '-translate-x-2 xl:-translate-x-5'),

                      isComingSoon &&
                      (screenHeight > 800
                        ? 'translate-x-0 xl:-translate-x-2'
                        : 'translate-x-0 mt-10'),

                      'px-8 flex justify-center -translate-y-6 transition-transform duration-500',
                    )}
                  >

                    <div
                      onClick={() => {
                        if (productAccessCheckData?.can_access) {
                          startReadingHandler({
                            bookId:
                              bookLandingDetails?.readersProductId as string,
                            chapterPageNumber: lastPageRead,
                          });
                          productAccessEnrollHandle();
                        } else {
                          showModal();
                        }
                      }}
                      className={containerClass}
                    >
                      <motion.div
                        className='absolute top-0 left-0 cursor-pointer h-full w-full'
                        style={{
                          background:
                            'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0) 100%)',
                          transform: 'translateX(-100%)',
                        }}
                        animate={{ translateX: ['-100%', '100%'] }}
                        transition={{
                          duration: 2,
                          ease: 'linear',
                          repeat: Infinity,
                        }}
                      />

                      <Button
                        style={{
                          border:
                            slidBookContainerData?.bookType !== 'comingSoon'
                              ? '1px solid #FFFFFF33'
                              : '1px solid #FFFFFF0D',
                        }}
                        className={clsx(
                          slidBookContainerData?.bookType === 'comingSoon'
                            ? 'bg-grayZl-850 border-[]'
                            : 'bg-[#7A7A7A]',
                          'w-full -z-10  text-white rounded-lg',
                        )}
                      >
                        <div className='relative'>
                          <span className='capitalize text-xs py-1 px-12 lg:px-0 font-medium'>
                            {(productAccessCheckData?.can_access &&
                              productAccessCheckData?.is_purchased) ||
                              (productAccessCheckData?.can_access &&
                                productAccessCheckData.user_product)
                              ? 'CONTINUE READING'
                              : productAccessCheckData?.can_access
                                ? 'START READING'
                                : 'READ SAMPLE'}
                          </span>
                        </div>
                      </Button>
                    </div>

                  </div>
                </ShouldRender>
              </ShouldRender>
              <ShouldRender
                check={
                  screens?.lg &&
                  extractSlugAndProductId(router.asPath) &&
                  slidBookContainerData?.bookType !== 'comingSoon'
                }
              >
                {/* <div className=''>
                  <div className='shadow-md text-white'>
                    <div className='flex gap-x-2'>
                      <ShouldRender
                        check={
                          slidBookContainerData?.hardCoverAvailable !== false
                        }
                      >
                        <motion.div
                          className={clsx(
                            `rounded-lg cursor-pointer p-3 w-full ${
                              selectedOption.type === 'hardCopy'
                                ? 'bg-white'
                                : 'bg-backgroundGray'
                            }`,
                          )}
                          style={{
                            boxShadow: '0px 4px 4px 0px #0000004D',
                            border: '1px solid #FFFFFF33',
                          }}
                          onClick={() =>
                            handleOptionClick({
                              type: 'hardCopy',
                              productId:
                                slidBookContainerData?.hardCoverProductId,
                              price:
                                slidBookContainerData?.hardCopyPrice
                                  ?.discountedPrice,
                            })
                          }
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          whileHover={{ scale: 1 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <div
                            className={clsx(
                              'flex items-center justify-between relative w-min h-full',
                            )}
                          >
                            <div
                              className={clsx(
                                selectedOption.type === 'hardCopy'
                                  ? 'text-black'
                                  : 'text-white',
                                `flex flex-col w-full`,
                              )}
                            >
                              <div className=' text-xs font-bold '>
                                HARD COPY
                              </div>
                              <div className='flex gap-x-2 w-full items-baseline pr-6'>
                                <span className='inline-flex font-bold text-lg'>
                                  <p>₹</p>{' '}
                                  {
                                    slidBookContainerData?.hardCopyPrice
                                      ?.discountedPrice
                                  }
                                </span>
                                <span className='line-through flex text-base  text-[#9A9A9A] font-semibold'>
                                  <p>₹</p>{' '}
                                  {
                                    slidBookContainerData?.hardCopyPrice
                                      ?.mrpPrice
                                  }
                                </span>
                              </div>
                              <div className='relative w-full mt-2  overflow-hidden rounded-lg'>
                                <ShouldRender
                                  check={selectedOption.type === 'hardCopy'}
                                >
                                  <motion.div
                                    className='absolute top-0 left-0 h-full w-full'
                                    style={{
                                      background:
                                        'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0) 100%)',
                                      transform: 'translateX(-100%)',
                                    }}
                                    animate={{ translateX: ['-100%', '100%'] }}
                                    transition={{
                                      duration: 2,
                                      ease: 'linear',
                                      repeat: Infinity,
                                    }}
                                  />
                                </ShouldRender>

                                <div
                                  className={clsx(
                                    selectedOption.type === 'hardCopy'
                                      ? 'bg-[#ECAD03] border-[1px] border-[#FFFFFF33] text-white '
                                      : 'bg-transparent  border-[1px] border-transparent text-[#9A9A9A] -translate-x-3',
                                    'text-xs   rounded-lg  font-bold whitespace-nowrap py-1 px-3 uppercase w-full ',
                                  )}
                                >
                                  Free digital copy
                                </div>
                              </div>
                            </div>
                            <label className='rounded-full flex items-start absolute -right-[2px] -top-[3px] justify-center cursor-pointer'>
                              <Checkbox
                                className='hidden'
                                checked={selectedOption.type === 'hardCopy'}
                              />
                              <div
                                className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                  selectedOption.type === 'hardCopy'
                                    ? 'bg-black'
                                    : 'bg-transparent border-2 border-[#737373]'
                                }`}
                              >
                                {selectedOption.type === 'hardCopy' && (
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth='4'
                                    className='w-3 h-3  text-white'
                                  >
                                    <path
                                      d='M5 12l5 5L20 7'
                                      strokeLinecap='round'
                                    />
                                  </svg>
                                )}
                              </div>
                            </label>
                          </div>
                        </motion.div>
                      </ShouldRender>
                      <ShouldRender
                        check={
                          slidBookContainerData?.hardCoverAvailable === false
                        }
                      >
                        <motion.div
                          className={clsx(`rounded-lg cursor-pointer  w-full `)}
                        >
                          <div className='w-full  h-full'>
                            <Button
                              size='small'
                              type='text'
                              style={{
                                position: 'relative',
                                height: '100%',
                                background: '#141414',
                                width: '100%',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '22px 30px',
                                zIndex: 1,
                                boxShadow: '0px 4px 4px 0px #0000004D',
                              }}
                              className='w-full  h-full  hover:text-white text-xs font-semibold'
                            >
                              <span
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  zIndex: -1,
                                  borderRadius: 'inherit',
                                  padding: '1.3px',
                                  background:
                                    'linear-gradient(180deg, #6199FF 0%, #045CFB 100%)',
                                  WebkitMask:
                                    'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',

                                  WebkitMaskComposite: 'xor',
                                  maskComposite: 'exclude',
                                }}
                              />
                              <div className='flex justify-center items-center w-full '>
                                <span className='flex flex-col justify-start gap-y-1.5'>
                                  <p className='w-full text-center font-semibold text-white text-[10px]'>
                                    Hard Copy
                                  </p>
                                  <span
                                    className='flex gap-x-2 font-bold text-xs items-baseline pl-1'
                                    style={{
                                      background:
                                        'linear-gradient(to bottom, #6199FF, #045CFB)',
                                      WebkitBackgroundClip: 'text',
                                      WebkitTextFillColor: 'transparent',
                                    }}
                                  >
                                    Available SOON
                                  </span>
                                </span>
                              </div>
                            </Button>
                          </div>
                        </motion.div>
                      </ShouldRender>
                      <motion.div
                        className={clsx(
                          `rounded-lg cursor-pointer p-3 w-full ${
                            selectedOption.type === 'softCopy'
                              ? 'bg-white'
                              : 'bg-backgroundGray'
                          }`,
                        )}
                        style={{
                          boxShadow: '0px 4px 4px 0px #0000004D',
                          border: '1px solid #FFFFFF33',
                        }}
                        onClick={() =>
                          handleOptionClick({
                            type: 'softCopy',
                            productId: slidBookContainerData?.digitalProductId,
                            price:
                              slidBookContainerData?.softCopyPrice
                                ?.discountedPrice,
                          })
                        }
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        whileHover={{ scale: 1 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div
                          className={clsx(
                            'flex items-center justify-between relative w-min h-full',
                          )}
                        >
                          <div
                            className={clsx(
                              selectedOption.type === 'softCopy'
                                ? 'text-black'
                                : 'text-white',
                              `flex flex-col w-full`,
                            )}
                          >
                            <div className=' text-xs font-bold '>
                              DIGITAL COPY
                            </div>
                            <div className='flex gap-x-2 w-full items-baseline '>
                              <span className='inline-flex font-bold text-lg'>
                                <p>₹</p>{' '}
                                {
                                  slidBookContainerData?.softCopyPrice
                                    ?.discountedPrice
                                }
                              </span>
                              <span className='line-through flex text-base  text-[#9A9A9A] font-semibold'>
                                <p>₹</p>{' '}
                                {slidBookContainerData?.softCopyPrice?.mrpPrice}
                              </span>
                            </div>
                            <div className='relative w-full mt-2 max-w-[256px] overflow-hidden rounded-lg'>
                              <ShouldRender
                                check={selectedOption.type === 'softCopy'}
                              >
                                <motion.div
                                  className='absolute top-0 left-0 h-full w-full'
                                  style={{
                                    background:
                                      'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0) 100%)',
                                    transform: 'translateX(-100%)',
                                  }}
                                  animate={{ translateX: ['-100%', '100%'] }}
                                  transition={{
                                    duration: 2,
                                    ease: 'linear',
                                    repeat: Infinity,
                                  }}
                                />
                              </ShouldRender>

                              <div
                                className={clsx(
                                  selectedOption.type === 'softCopy'
                                    ? 'bg-[#ECAD03] border-[1px] border-[#FFFFFF33] text-white '
                                    : 'bg-transparent  border-[1px] border-transparent text-[#9A9A9A] -translate-x-3',
                                  'text-xs   rounded-lg  font-bold whitespace-nowrap py-1 px-6 uppercase w-full ',
                                )}
                              >
                                Instant Access
                              </div>
                            </div>
                          </div>
                          <label className='rounded-full flex items-start absolute -right-[2px] -top-[3px] justify-center cursor-pointer'>
                            <Checkbox
                              className='hidden'
                              checked={selectedOption.type === 'softCopy'}
                            />
                            <div
                              className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                selectedOption.type === 'softCopy'
                                  ? 'bg-black'
                                  : 'bg-transparent border-2 border-[#737373]'
                              }`}
                            >
                              {selectedOption.type === 'softCopy' && (
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                  stroke='currentColor'
                                  strokeWidth='4'
                                  className='w-3 h-3  text-white'
                                >
                                  <path
                                    d='M5 12l5 5L20 7'
                                    strokeLinecap='round'
                                  />
                                </svg>
                              )}
                            </div>
                          </label>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div> */}

                {isOrgMember ? (
                  <div className='w-full flex flex-col gap-2.5'>
                    {/* Corporate Option Cards matching normal pricing button styles */}
                    <div className='grid grid-cols-2 gap-2.5 w-full'>
                      {/* Hardcopy + Digital Card - Primary Highlighted */}
                      <div className='relative w-[160px] flex flex-1 rounded-lg h-full'>
                        <ButtonDefault
                          size={0}
                          isWidthFull={true}
                          rounded={false}
                          roundedSm={true}
                          variant={ButtonVariants.WHITE}
                          className='px-[7px] py-2 min-h-[66px] w-[160px]'
                          onClick={(e) => {
                            e.stopPropagation();
                            const hardId =
                              ecomProductData?.id ||
                              slidBookContainerData?.hardCoverProductId ||
                              bookLandingDetails?.hardCoverProductId ||
                              4531;
                            router.push(`/corporate-checkout?productId=${hardId}`);
                          }}
                        >
                          <div className='active:scale-95 transition-transform cursor-pointer relative w-full flex flex-col gap-1 rounded-lg'>
                            <div className='flex items-center gap-2.5 justify-center'>
                              <p className={clsx(montserrat.className, 'text-sm font-semibold text-black text-center line-clamp-1')}>
                                Hardcopy + Digital
                              </p>
                            </div>
                            <div className='flex items-center gap-1 justify-center'>
                              <p className={clsx(montserrat.className, 'text-center text-base/6 font-semibold text-black whitespace-nowrap')}>
                                ₹{hardCorporatePrice.unit_price}
                              </p>
                              <p className={clsx(montserrat.className, 'text-center text-sm/6 font-medium text-grayZl-700 line-through whitespace-nowrap')}>
                                ₹{hardCorporatePrice.regular_price}
                              </p>
                            </div>
                          </div>
                        </ButtonDefault>
                      </div>

                      {/* Digital Copy Card */}
                      <div className='relative w-[160px] flex flex-1 rounded-lg h-full'>
                        <ButtonDefault
                          size={0}
                          isWidthFull={true}
                          rounded={false}
                          roundedSm={true}
                          variant={ButtonVariants.GRAY}
                          className='px-[7px] py-2 min-h-[66px] w-[160px]'
                          onClick={(e) => {
                            e.stopPropagation();
                            const digitalId =
                              ecomProductData?.digital_pair?.id ||
                              slidBookContainerData?.digitalProductId ||
                              bookLandingDetails?.digitalProductId ||
                              4530;
                            router.push(`/corporate-checkout?productId=${digitalId}`);
                          }}
                        >
                          <div className='active:scale-95 transition-transform cursor-pointer relative w-full flex flex-col gap-1 rounded-lg'>
                            <div className='flex items-center gap-2.5 justify-center'>
                              <p className={clsx(montserrat.className, 'text-sm font-semibold text-white text-center line-clamp-1')}>
                                Digital Copy
                              </p>
                            </div>
                            <div className='flex items-center gap-1 justify-center'>
                              <p className={clsx(montserrat.className, 'text-center text-base/6 font-semibold text-white whitespace-nowrap')}>
                                ₹{digitalCorporatePrice.unit_price}
                              </p>
                              <p className={clsx(montserrat.className, 'text-center text-sm/6 font-medium text-grayZl-700 line-through whitespace-nowrap')}>
                                ₹{digitalCorporatePrice.regular_price}
                              </p>
                            </div>
                          </div>
                        </ButtonDefault>
                      </div>
                    </div>

                    {/* EXCLUSIVE BUSINESS PRICE Banner */}
                    <div className='w-full relative flex items-center justify-center mt-2'>
                      <div
                        className='w-full py-2 px-6 flex items-center justify-center relative overflow-hidden'
                        style={{
                          background:
                            'linear-gradient(90deg, #000000 0%, #150400 12%, #380E01 32%, #7A2102 55%, #C23A05 78%, #FF5612 100%)',
                          clipPath: 'polygon(0 0, calc(100% - 7px) 0, calc(100% - 39px) 100%, 0 100%)',
                        }}
                      >
                        <span
                          className={clsx(
                            montserrat.className,
                            'text-xs sm:text-sm font-medium tracking-wider uppercase italic text-white drop-shadow-sm select-none',
                          )}
                        >
                          EXCLUSIVE BUSINESS PRICE
                        </span>
                      </div>
                      {/* Slanted Accent Stripe */}
                      <div
                        className='absolute inset-0 pointer-events-none'
                        style={{
                          background: '#FF5612',
                          clipPath:
                            'polygon(calc(100% - 3px) 0, 100% 0, calc(100% - 32px) 100%, calc(100% - 35px) 100%)',
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    className={twMerge(
                      'grid grid-cols-2 gap-2.5 w-full',
                      (!hasDigitalPair || hasUpgradeOffer) ? 'grid-cols-1 place-items-center' : '',
                    )}
                  >
                    <ShouldRender check={hasUpgradeOffer}>
                      <div className='relative w-[330px] rounded-lg h-full'>
                        <ButtonDefault
                          size={0}
                          isWidthFull={false}
                          rounded={false}
                          roundedSm={true}
                          variant={ButtonVariants.WHITE}
                          enableScaling={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            const hardId =
                              ecomProductData?.id ||
                              slidBookContainerData?.hardCoverProductId ||
                              bookLandingDetails?.hardCoverProductId;
                            router.push({
                              pathname: '/checkout/upgrad-to-hardcopy',
                              query: { productId: hardId },
                            });
                          }}
                          className='px-[7px] py-2 min-h-[66px] w-[330px] border-none'
                        >
                          <div className='active:scale-95 transition-transform cursor-pointer relative w-full flex flex-col gap-1 rounded-lg'>
                            <div className='flex items-center gap-2.5 justify-center'>
                              <p className='text-sm font-semibold text-black text-center line-clamp-1'>
                                Upgrade to Hardcopy
                              </p>
                            </div>
                            <div className='flex items-center gap-1 justify-center'>
                              <p className='text-center text-base/6 font-semibold text-black whitespace-nowrap'>
                                {hardCopyCountryData?.currency_symbol || '₹'}
                                {ecomProductData?.upgrade_offer?.upgrade_price || 1000}
                              </p>
                              {hardCopyCountryData?.stroked_price ? (
                                <p className='text-center text-sm/6 font-medium text-grayZl-700 line-through whitespace-nowrap'>
                                  {hardCopyCountryData?.currency_symbol || '₹'}
                                  {hardCopyCountryData?.stroked_price}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </ButtonDefault>
                      </div>
                    </ShouldRender>

                    <ShouldRender check={!hasUpgradeOffer}>
                      <div
                        className={twMerge(
                          'relative w-[160px] flex flex-1 rounded-lg h-full',
                          !hasDigitalPair ? 'w-[330px]' : '',
                        )}
                      >
                        <ShouldRender
                          check={
                            slidBookContainerData?.hardCoverAvailable !== false
                          }
                        >
                          <ButtonDefault
                            size={0}
                            isWidthFull={true}
                            rounded={false}
                            roundedSm={true}
                            variant={
                              hardCopyCountryData?.is_available !== null
                                ? ButtonVariants.WHITE
                                : ButtonVariants.UNSTYLED
                            }
                            available={hardCopyCountryData?.is_available !== null}
                            loading={
                              Number(slidBookContainerData?.hardCoverProductId) ===
                              loadingAddToCart.id && loadingAddToCart.value
                            }
                            disabled={
                              Number(slidBookContainerData?.hardCoverProductId) ===
                              loadingAddToCart.id && loadingAddToCart.value
                            }
                            enableScaling={false}
                            unavailableText='Hard Copy'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddDataToCart({
                                payload: {
                                  id: Number(
                                    slidBookContainerData?.hardCoverProductId,
                                  ) as number,
                                  temp_user_id: localUserId,
                                  quantity: 1,
                                  trackingData: {
                                    productData: {
                                      price: Number(
                                        slidBookContainerData?.hardCopyPrice
                                          ?.discountedPrice,
                                      ),
                                      id: Number(
                                        slidBookContainerData?.hardCoverProductId,
                                      ),
                                      heading: `${ecomProductData?.display_name}`,
                                      quantity: 1 as number,
                                      currency:
                                        hardCopyCountryData?.i18n_attributes?.currency,
                                      category: landingPageData?.categories?.[0]?.name,
                                    },
                                    userData,
                                    pathname: router.pathname,
                                    productType: 'hardcopy',
                                    type: 'books',
                                  },
                                },
                                bookData: {
                                  bookName: slidBookContainerData?.bookName,
                                  productType: 'hardcopy',
                                },
                              });
                            }}
                            className={clsx(
                              hardCopyCountryData?.is_available !== null &&
                              'disabled:bg-white/70 disabled:border-white/70',
                              'px-[7px] py-2 min-h-[66px]',
                              !hasDigitalPair ? 'w-[330px]' : 'w-[160px]',
                            )}
                          >
                            <div className='active:scale-95 transition-transform cursor-pointer relative w-full flex flex-col gap-1  rounded-lg'>
                              <div className='flex items-center gap-2.5 justify-center'>
                                <p className='text-sm font-semibold text-black text-center line-clamp-1'>
                                  {isOrange ? (
                                    <>
                                      <span className='capitalize'>
                                        {mainButtonTitle}
                                      </span>
                                      {hasDigitalPair && ' + Digital'}
                                    </>
                                  ) : (
                                    <RenderIf
                                      fallBack='Paperback'
                                      condition={
                                        bookLandingDetails?.bookType !== 'paperBack'
                                      }
                                    >
                                      Hardcopy
                                      <ShouldRender
                                        check={
                                          hasDigitalPair &&
                                          hardCopyCountryData?.is_available !== null
                                        }
                                      >
                                        {' '}
                                        + Digital
                                      </ShouldRender>
                                    </RenderIf>
                                  )}
                                </p>
                              </div>
                              <div className='flex items-center gap-1 justify-center'>
                                <p className='text-center text-base/6 font-semibold text-black whitespace-nowrap'>
                                  {hardCopyCountryData?.currency_symbol}
                                  {hardCopyCountryData?.main_price}
                                </p>
                                <p className='text-center text-sm/6 font-medium text-grayZl-700 line-through whitespace-nowrap'>
                                  {hardCopyCountryData?.currency_symbol}
                                  {hardCopyCountryData?.stroked_price}
                                </p>
                              </div>
                            </div>
                          </ButtonDefault>
                        </ShouldRender>

                        {/* Avaiable Soon */}
                        <ShouldRender
                          check={
                            slidBookContainerData?.hardCoverAvailable === false
                          }
                        >
                          <div className='w-full bg-black rounded-lg border-[1.5px] border-grayZl-850 h-full flex flex-col items-center justify-center'>
                            <p className='text-xs/5 font-semibold text-center'>
                              Hard Copy
                            </p>
                            <p className='text-xs/5 font-medium  text-transparent bg-[linear-gradient(180deg,#F9742C_0%,#F20486_100%)] bg-clip-text'>
                              Available Soon
                            </p>
                          </div>
                        </ShouldRender>
                      </div>
                    </ShouldRender>
                    <ShouldRender
                      check={hasDigitalPair && !hasUpgradeOffer}
                    >
                      {/* Soft Copy Button */}
                      <div className='relative w-[160px] flex flex-1 rounded-lg h-full'>
                        <ButtonDefault
                          size={0}
                          isWidthFull={true}
                          rounded={false}
                          roundedSm={true}
                          variant={
                            effectiveDigitalCopyData?.is_available !== null
                              ? ButtonVariants.GRAY
                              : ButtonVariants.UNSTYLED
                          }
                          loading={
                            Number(digitalId) ===
                            loadingAddToCart.id && loadingAddToCart.value
                          }
                          disabled={
                            Number(digitalId) ===
                            loadingAddToCart.id && loadingAddToCart.value
                          }
                          available={
                            effectiveDigitalCopyData?.is_available !== null
                          }
                          enableScaling={false}
                          unavailableText='Digital Copy'
                          onClick={(e) => {
                            e.stopPropagation();

                            if (productAccessCheckData?.can_access) {
                              startReadingHandler({
                                bookId:
                                  bookLandingDetails?.readersProductId as string,
                                chapterPageNumber: lastPageRead || undefined,
                              });
                              productAccessEnrollHandle();
                              return;
                            }

                            handleAddDataToCart({
                              payload: {
                                id: Number(digitalId) as number,
                                temp_user_id: localUserId,
                                quantity: 1,
                                trackingData: {
                                  productData: {
                                    price: Number(
                                      effectiveDigitalCopyData?.main_price ||
                                      slidBookContainerData?.softCopyPrice?.discountedPrice,
                                    ),
                                    id: Number(digitalId),
                                    heading: `${ecomProductData?.digital_pair?.display_name || ecomProductData?.digital_pair?.name || ecomProductData?.display_name}`,
                                    quantity: 1 as number,
                                    currency:
                                      effectiveDigitalCopyData?.currency_symbol ||
                                      hardCopyCountryData?.i18n_attributes?.currency,
                                    category: landingPageData?.categories?.[0]?.name,
                                  },
                                  userData,
                                  pathname: router.pathname,
                                  productType: 'digitalcopy',
                                  type: 'books',
                                },
                              },
                              bookData: {
                                bookName: ecomProductData?.digital_pair?.name || slidBookContainerData?.bookName,
                                productType: 'digitalcopy',
                              },
                            });
                          }}
                          className='min-h-[66px] w-[160px] px-3 py-2.5'
                        >
                          <div className='cursor-pointer active:scale-95 transition-transform flex-1 flex flex-col gap-1 rounded-lg'>
                            <div className='flex items-center justify-center gap-2.5'>
                              <p className='text-center text-sm font-semibold text-white whitespace-nowrap'>
                                Digital Copy
                              </p>
                            </div>
                            {productAccessCheckData?.can_access ? (
                              <p className='text-center text-xs/5 font-medium text-success-500  whitespace-nowrap'>
                                Subscribed • Read Now
                              </p>
                            ) : (
                              <div className='flex items-center gap-1 justify-center'>
                                <p className='text-base/6 font-semibold whitespace-nowrap'>
                                  {effectiveDigitalCopyData?.currency_symbol}
                                  {effectiveDigitalCopyData?.main_price}
                                </p>
                                <p className='text-sm/6 font-medium text-grayZl-700 line-through whitespace-nowrap'>
                                  {effectiveDigitalCopyData?.currency_symbol}{' '}
                                  {effectiveDigitalCopyData?.stroked_price}
                                </p>
                              </div>
                            )}
                          </div>
                        </ButtonDefault>
                      </div>
                    </ShouldRender>
                  </div>
                )}
              </ShouldRender>
              <ShouldRender
                check={slidBookContainerData?.bookType !== 'comingSoon'}
              >
                <ShouldRender check={screens?.lg && !screens.xl}>
                  <Affix offsetBottom={screens.lg ? 28 : 0}>
                    {paymentButton}
                    <div
                      className={clsx(
                        isSubscriptionDisabled() ? 'pt-0' : 'pt-3',
                        'text-white flex gap-x-2 text-xs justify-center',
                      )}
                    >
                      <SecurePaymentV2 />
                      <p className='leading-[21px]'>Secure Payments</p>
                    </div>
                  </Affix>
                </ShouldRender>
                <ShouldRender check={screens.xl}>
                  {paymentButton}
                  <div className='text-white flex gap-x-2 text-xs justify-center pt-3'>
                    <SecurePaymentV2 />
                    <p className='leading-[21px]'>Secure Payments</p>
                  </div>
                </ShouldRender>
              </ShouldRender>
              <ShouldRender
                check={
                  slidBookContainerData?.bookType === 'comingSoon' && screens.lg
                }
              >
                <div
                  className={clsx(
                    montserrat.className,
                    ' text-white p-4 pb-0 -translate-y-7  xl:-translate-y-5 rounded-md ',
                  )}
                >
                  <div className='flex items-center justify-center font- space-x-1 mb-2 xl:mb-3 text-sm text-[#F87171] '>
                    <TimeRunningClock />
                    <span className='  text-sm leading-[1.375rem]'>
                      Prebook Now, Pay the Lowest
                    </span>
                  </div>

                  <div className='text-white text-sm font-light text-center mb-1 xl:mb-2 leading-[1.375rem]'>
                    Hard Copy + Digital Copy
                  </div>
                  <ShouldRender
                    check={
                      getGlobalCountry()?.name?.toLowerCase() === 'india' ||
                      isPreOrderAvailable
                    }
                  >
                    <div className='flex items-end justify-center  space-x-2'>
                      <span className='flex xxxs:gap-x-1 md:gap-x-2 items-center pl-1'>
                        <p
                          className='text-white text-2xl font-semibold leading-8 line-through'
                          style={{ textDecorationColor: 'red' }}
                        >
                          {hardCopyCountryData?.currency_symbol}
                          {hardCopyCountryData?.main_price}
                        </p>
                        <p
                          className={clsx(
                            montserrat.className,
                            'line-through text-white/[.60] text-center font-medium leading-6 ',
                          )}
                        >
                          {hardCopyCountryData?.currency_symbol}
                          {hardCopyCountryData?.stroked_price}
                        </p>

                        {hardCopyCountryData?.main_price &&
                          hardCopyCountryData?.stroked_price ? (
                          <p className='hidden xxxs:flex gap-x-1 pl-1 md:pl-0 text-[#16A34A] font-bold text-[10px] md:text-sm items-center'>
                            {Math.round(
                              ((hardCopyCountryData?.stroked_price -
                                Number(hardCopyCountryData?.main_price)) /
                                hardCopyCountryData?.stroked_price) *
                              100,
                            )}
                            %{' '}
                            <ArrowCircleDownIcon className='w-3 h-3 md:w-5 md:h-5' />
                          </p>
                        ) : null}
                      </span>
                    </div>
                  </ShouldRender>
                </div>
                <div className=' -translate-y-4 xl:-translate-y-0'>
                  <div className='space-y-3'>
                    <ButtonDefault
                      isWidthFull={true}
                      size={4}
                      variant={
                        isPreOrderAvailable
                          ? ButtonVariants.PRIMARY
                          : ButtonVariants.UNSTYLED
                      }
                      is3D={isPreOrderAvailable}
                      unavailableText='Pre-Order'
                      available={isPreOrderAvailable}
                      loading={loadingAddToCart.value}
                      disabled={loadingAddToCart.value}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddDataToCart({
                          payload: {
                            id: Number(
                              slidBookContainerData?.hardCoverProductId,
                            ) as number,
                            temp_user_id: localUserId,
                            quantity: 1,
                            isBuyNow: true,
                            trackingData: {
                              productData: {
                                price: Number(
                                  slidBookContainerData?.hardCopyPrice
                                    ?.discountedPrice,
                                ),
                                id: Number(
                                  slidBookContainerData?.hardCoverProductId,
                                ),
                                heading: `${ecomProductData?.display_name}`,
                                quantity: 1 as number,
                                currency:
                                  hardCopyCountryData?.i18n_attributes?.currency,
                                category: landingPageData?.categories?.[0]?.name,
                              },
                              userData,
                              pathname: router.pathname,
                              productType: 'hardcopy',
                              type: 'books',
                            },
                          },
                          isBuyNow: true,
                        });
                      }}
                      className='w-full p-0 py-0 px-0'
                    >
                      <div className='w-full flex justify-center font-semibold leading-6 items-center gap-1 py-[3px] px-3.5'>
                        Pre-Order at {hardCopyCountryData?.currency_symbol}
                        {hardCopyCountryData?.preorder_display_price}
                      </div>
                    </ButtonDefault>
                    {claimSubscriptionButton}
                  </div>
                  <div className='flex justify-center mt-3'>
                    <ul className='text-white text-sm font-light space-y-1 list-disc list-inside'>
                      <li>Digital Book will be delivered instantly.</li>
                      <li>Pre Orders will be shipped in 15-30 days.</li>
                    </ul>
                  </div>
                </div>
              </ShouldRender>
            </ShouldRender>

            {/* <ShouldRender check={isPurchased && screens.lg}>
              <div className='flex pb-3 pt-4 2xl:pt-8'>
                <ButtonDefault
                  size={4}
                  rounded={false}
                  roundedSm={true}
                  onClick={() => {
                    router.push(
                      `/pdf/${slidBookContainerData?.readersProductId}?chapterPageNumber=${lastPageRead || 1}`,
                    );
                  }}
                  variant={ButtonVariants.WHITE}
                  className='flex w-full justify-center  px-5 py-1 min-w-[310px] border-none font-medium text-xs whitespace-nowrap'
                >
                  <span className='px-4 text-sm md:text-base font-semibold '>
                    {lastPageRead === null || +lastPageRead === 1
                      ? 'Start'
                      : 'Continue'}{' '}
                    Reading
                  </span>
                </ButtonDefault>
              </div>
            </ShouldRender> */}
            <ShouldRender check={screens.lg && short}>
              <div className='flex flex-col gap-y-2 pb-3'>
                <ButtonDefault
                  size={4}
                  rounded={false}
                  roundedSm={true}
                  onClick={() => {
                    if (token) {
                      router.push(
                        `/pdf/${slidBookContainerData?.readersProductId}?samplePdf=true`,
                      );
                    } else {
                      setVisible((prevState) => ({
                        ...prevState,
                        signUp: true,
                        bookId:
                          slidBookContainerData?.readersProductId as string,
                      }));
                    }
                  }}
                  variant={ButtonVariants.WHITE}
                  className='flex w-full justify-center  px-5 py-3 min-w-[310px] border-none font-medium text-xs whitespace-nowrap'
                >
                  <span className='px-4 text-sm md:text-base font-semibold py-1'>
                    Start Reading
                  </span>
                </ButtonDefault>

                {/* <ButtonDefault
                      size={4}
                      rounded={false}
                      roundedSm={true}
                      variant={ButtonVariants.GRAY}
                      className='flex w-full justify-center  px-5 py-3 border-none font-medium text-xs whitespace-nowrap'
                    >
                      <span className='px-4 text-sm md:text-base font-semibold flex gap-x-2 items-center py-1'>
                        <svg
                          width='24'
                          height='24'
                          viewBox='0 0 24 24'
                          fill='none'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path
                            d='M5.41528 4.83167C5.41528 4.0405 6.29053 3.56266 6.95604 3.99049L18.1068 11.1588C18.7191 11.5525 18.7191 12.4475 18.1068 12.8412L6.95604 20.0095C6.29053 20.4373 5.41528 19.9595 5.41528 19.1683V4.83167Z'
                            stroke='white'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                        Listen
                      </span>
                    </ButtonDefault> */}
              </div>
            </ShouldRender>

            <Modal
              open={isModalVisible}
              onOk={handleOk}
              onCancel={handleCancel}
              footer={null}
              width='100%'
              className='custom-modal-bg customLoginModal'
              closable={false}
              style={{ top: 0, backgroundColor: 'transparent' }}
              centered={true}
              destroyOnHidden
            >
              <div
                className='no-select custom-modal-bg w-full'
                id='trackingModal'
                style={{ minHeight: '90vh' }}
              >
                <PDFReaderIframe
                  isModal={true}
                  handleCancel={handleCancel}
                  bookSlugId={router.query.bookId as string}
                  isSampleReadingPag={true}
                />
              </div>
            </Modal>
          </div>
        </div>
        <div className='absolute bg-gradient-to-b from-[rgba(0,0,0,0.60)] via-[rgba(0,0,0,0.88)] backdrop-blur-2xl  inset-0 w-full h-full lg:hidden block  -z-20'></div>

        <div className='absolute  inset-0 lg:hidden top-0 block backdrop-blur-lg -translate-y-[250px] md:-translate-y-[450px] lg:translate-y-[100px] h-full -z-40 overflow-hidden'>
          <Image
            src={slidBookContainerData?.bookImage as string}
            alt='Background Image'
            layout='fill'
            priority
            className='-rotate-90 lg:rotate-90 transform w-auto h-auto absolute top-0 left-0 object-contain'
          />
        </div>
      </div>
    </ConfigProvider>
  );
};

export default SlidBookContainer;
